/**
 * FE-transient row metadata + column field-name constants for the xVT (xVT Planning Tool) forecast-input grid.
 *
 * Naming idiom mirrors forecast's `ForecastGridFixedFields`
 * (`src/features/business-group/forecast-template/forecast-utils/ForecastGridConstants.ts`):
 * each entry is `{ value, displayName }` where `value` is the actual row/column key.
 *
 * IMPORTANT: xVT rows are FLAT / columnar. Unlike forecast, they have NO nested
 * `corp_segments[]` / `bus_segments[]` / `forecast_months[]` arrays. Do NOT import or depend on
 * forecast's `ForecastGridFixedFields` or its nested shape here - xVT owns its own field set.
 *
 * The FE-transient metadata field NAMES (`row_id`, `is_new_fe_row`, `is_touched`, `is_edited`)
 * intentionally match the forecast idiom so grid-state logic (dirty tracking, submission filters)
 * can stay feature-agnostic where applicable. These transient fields are FE-only and are NOT
 * persisted to the backend / export payload.
 *
 * Column list corresponds to PRD Table 1 plus backend-provided export/read-only flags.
 */
export const XvtGridFixedFields = {
  // --- FE-transient row metadata (FE-only, not persisted) ---
  RowId: { value: 'row_id', displayName: 'Row Id' },
  IsNewFERow: { value: 'is_new_fe_row', displayName: 'Is New FE Row' },
  IsTouched: { value: 'is_touched', displayName: 'Is Touched' },
  IsEdited: { value: 'is_edited', displayName: 'Is Edited' },
  IsActive: { value: 'is_active', displayName: 'Is Active' },
  // Transient validation flag - MM-YY monthly spread does not total 100%. Drives the row marker bar
  // (see `XvtGridConstants`); advisory - see `XvtMmYySumValidationService`.
  // The leading `__` marks it as FE-only by convention; what actually keeps it off the wire is the
  // explicit key allowlist in `prepareXvtRowForUpload`.
  XvtMmYyWarning: { value: '__xvtMmYyWarning', displayName: 'MM-YY Total Off Target' },

  // --- ID / tracking (backend-provided when present) ---
  ScenarioSeqId: { value: 'scenario_seq_id', displayName: 'Scenario Seq Id' },
  XvtForecastInputId: { value: 'xvt_forecast_input_id', displayName: 'Line Item ID' },

  // --- Column 1-7: dropdown / auto / freeform metadata columns (PRD Table 1) ---
  ProductLine: { value: 'product_line', displayName: 'Product Line' },
  ProjectCode: { value: 'project_code', displayName: 'Project Code' },
  ExpenseType: { value: 'expense_type', displayName: 'Expense Type' },
  BudgetOwner: { value: 'budget_owner', displayName: 'Budget Owner' },
  CostCenter: { value: 'cost_center', displayName: 'Cost Center' },
  Description: { value: 'description', displayName: 'Description' },
  MainBuild: { value: 'main_build', displayName: 'Main Build' },

  // --- Derived/fixed Item Details presentation columns (not persisted as separate row fields) ---
  CostCenterDescription: { value: 'cost_center_description', displayName: 'Cost Center Description' },
  ProductLineDescription: { value: 'product_line_description', displayName: 'Product Line Description' },
  AccountCode: { value: 'account_code', displayName: 'Account Code' },
  AccountCodeDescription: { value: 'account_code_description', displayName: 'Account Code Description' },
  ProjectCodeDescription: { value: 'project_code_description', displayName: 'Project Code Description' },
  Company: { value: 'company', displayName: 'Company' },
  CompanyDescription: { value: 'company_description', displayName: 'Company Description' },

  // --- Column 8-12: numeric input columns (yield / factor inputs) ---
  SmtOnlyYieldedQty: { value: 'smt_only_yielded_qty', displayName: 'SMT Only Yielded Quantity' },
  DeviceYieldedQty: { value: 'device_yielded_qty', displayName: 'Device Yielded Quantity' },
  SmtFactoryYieldPct: { value: 'smt_factory_yield_pct', displayName: 'SMT Factory Yield %' },
  DeviceFactoryYieldPct: { value: 'device_factory_yield_pct', displayName: 'Device Factory Yield %' },
  EoOtherFactorsPct: { value: 'eo_other_factors_pct', displayName: 'E&O & Other Factors %' },

  // --- Column 13-14: auto-calc (read-only, computed via XvtFormulas) ---
  SmtDriveQty: { value: 'smt_drive_qty', displayName: 'SMT Drive Quantity' },
  DeviceDriveQty: { value: 'device_drive_qty', displayName: 'Device Drive Quantity' },

  // --- Column 15-18: numeric input columns ($/Unit inputs) ---
  SmtBomPerUnit: { value: 'smt_bom_per_unit', displayName: 'SMT BOM ($/Unit)' },
  SmtOnlyMvaPerUnit: { value: 'smt_only_mva_per_unit', displayName: 'SMT Only MVA ($/Unit)' },
  FatpBomPerUnit: { value: 'fatp_bom_per_unit', displayName: 'FATP BOM ($/Unit)' },
  TotalMvaPerUnit: { value: 'total_mva_per_unit', displayName: 'Total MVA ($/Unit)' },

  // --- Column 19-26: auto-calc (read-only, computed via XvtFormulas) ---
  SmtBomCost: { value: 'smt_bom_cost', displayName: 'SMT BOM Cost $' },
  SmtOnlyMvaCost: { value: 'smt_only_mva_cost', displayName: 'SMT Only MVA Cost $' },
  SmtBomMvaTotalCost: { value: 'smt_bom_mva_total_cost', displayName: 'SMT BOM +MVA Total $' },
  FatpBomCost: { value: 'fatp_bom_cost', displayName: 'FATP BOM Cost $' },
  DeviceMvaCost: { value: 'device_mva_cost', displayName: 'Device MVA Cost $' },
  FatpBomMvaTotalCost: { value: 'fatp_bom_mva_total_cost', displayName: 'FATP BOM MVA Total $' },
  TotalBuildCostTotal: { value: 'total_build_cost_total', displayName: 'Total Build Cost Total $' },
  TotalBuildCostPerUnit: { value: 'total_build_cost_per_unit', displayName: 'Total Build Cost Per Unit $' },

  // --- Column 27: freeform text ---
  CommentsJustifications: { value: 'comments_justifications', displayName: 'Comments/Justifications' },

  // --- Export / read-only flags (backend-set, returned in row payload) ---
  IsExported: { value: 'is_exported', displayName: 'Is Exported' },
  ExportedAt: { value: 'exported_at', displayName: 'Exported At' },

  // --- Audit (backend-provided) ---
  UpdatedAt: { value: 'updated_at', displayName: 'Updated At' },
  UpdatedBy: { value: 'updated_by', displayName: 'Updated By' }
} as const;

export type XvtGridFixedFieldKeys = keyof typeof XvtGridFixedFields;
export type XvtGridFixedFieldValues = (typeof XvtGridFixedFields)[XvtGridFixedFieldKeys]['value'];

/**
 * A single flat/columnar xVT forecast-input grid row.
 *
 * Covers the ~29 PRD Table 1 fields (dropdown/auto/numeric/freeform + auto-calc), the dynamic MM-YY
 * monthly spread, FE-transient metadata, and the backend-provided export/read-only + audit flags.
 *
 * Numeric fields are `number | null` because AG Grid cells may be empty; `cellValueParser` yields
 * `null` for empty/invalid input. Auto-calc fields are also `number | null` here (the row shape) even
 * though their live grid values are derived via `valueGetter` from `XvtFormulas` - the backend export
 * payload may carry persisted values.
 *
 * The trailing index signature holds the dynamic MM-YY monthly-spread columns, which are generated
 * from the planning cycle's month range and keyed by the
 * month id (`YYYYMM01` format, matching the forecast month-header convention). Because named members
 * must conform to the index type, the signature widens to the union of all row value types.
 */
export interface XvtGridRowData {
  // FE-transient metadata (FE-only)
  row_id: string;
  is_new_fe_row: boolean;
  is_touched: boolean;
  is_edited: boolean;
  is_active: boolean;
  __xvtMmYyWarning?: boolean;

  // ID / tracking
  scenario_seq_id: number | null;
  xvt_forecast_input_id: number | null;

  // Columns 1-7
  product_line: string | null;
  project_code: string | null;
  expense_type: string | null;
  budget_owner: string | null;
  cost_center: string | null;
  description: string | null;
  main_build: string | null;

  // Fixed Item Details/report metadata
  company: string;

  // Columns 8-12 (numeric inputs)
  smt_only_yielded_qty: number | null;
  device_yielded_qty: number | null;
  smt_factory_yield_pct: number | null;
  device_factory_yield_pct: number | null;
  eo_other_factors_pct: number | null;

  // Columns 13-14 (auto-calc)
  smt_drive_qty: number | null;
  device_drive_qty: number | null;

  // Columns 15-18 (numeric inputs)
  smt_bom_per_unit: number | null;
  smt_only_mva_per_unit: number | null;
  fatp_bom_per_unit: number | null;
  total_mva_per_unit: number | null;

  // Columns 19-26 (auto-calc)
  smt_bom_cost: number | null;
  smt_only_mva_cost: number | null;
  smt_bom_mva_total_cost: number | null;
  fatp_bom_cost: number | null;
  device_mva_cost: number | null;
  fatp_bom_mva_total_cost: number | null;
  total_build_cost_total: number | null;
  total_build_cost_per_unit: number | null;

  // Column 27 (freeform)
  comments_justifications: string | null;

  // Export / read-only flags (backend-set)
  is_exported: boolean;
  exported_at: string | null;

  // Audit (backend-provided)
  updated_at: string | null;
  updated_by: string | null;

  // Dynamic MM-YY monthly-spread columns (keyed by `YYYYMM01` month id) + widened for named members
  [key: string]: string | number | boolean | null | undefined;
}
